/**
 * Dashboard Remote Data Source Implementation
 * Implementa acesso aos dados do dashboard via API
 */

export interface DashboardRemoteDataSource {
  fetchStats(): Promise<any>;
  fetchMetrics(period?: string): Promise<any>;
  fetchStatsByPeriod(startDate: string, endDate: string): Promise<any>;
}

import { getAuthToken } from '../../../../../core/services/auth/authToken';
import environment from '../../../../../config/environment';

export class DashboardRemoteDataSourceImpl implements DashboardRemoteDataSource {
  // Use API URL from environment configuration to keep protocols/hosts consistent
  private baseUrl = environment.getApiUrl();

  async fetchStats(): Promise<any> {
    console.log('🌐 Buscando estatísticas do dashboard...');
    
    try {
    const response = await fetch(`${this.baseUrl}/dashboard/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
      ...(getAuthToken() ? { 'Authorization': `Bearer ${getAuthToken()}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Estatísticas obtidas da API');
      return data;
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas:', error);
      // Retornar dados mock em caso de erro (fallback)
      return this.getMockStats();
    }
  }

  async fetchMetrics(period: string = 'month'): Promise<any> {
    console.log('📈 Buscando métricas do dashboard...', { period });
    
    try {
    const response = await fetch(`${this.baseUrl}/dashboard/metrics?period=${period}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
      ...(getAuthToken() ? { 'Authorization': `Bearer ${getAuthToken()}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Métricas obtidas da API');
      return data;
    } catch (error) {
      console.error('❌ Erro ao buscar métricas:', error);
      // Retornar dados mock em caso de erro (fallback)
      return this.getMockMetrics();
    }
  }

  async fetchStatsByPeriod(startDate: string, endDate: string): Promise<any> {
    console.log('📅 Buscando estatísticas por período...', { startDate, endDate });
    
    try {
      const url = new URL(`${this.baseUrl}/dashboard/stats/period`);
      url.searchParams.append('start_date', startDate);
      url.searchParams.append('end_date', endDate);
      
    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
      ...(getAuthToken() ? { 'Authorization': `Bearer ${getAuthToken()}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Estatísticas por período obtidas da API');
      return data;
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas por período:', error);
      // Retornar dados mock em caso de erro (fallback)
      return this.getMockStats();
    }
  }

  // ============ HELPER METHODS ============
  
  // getToken() removed in favor of centralized getAuthToken()

  // ============ MOCK DATA (FALLBACK) ============
  
  private getMockStats(): any {
    return {
      total_customers: 150,
      total_revenue: 25000.50,
      monthly_growth: 12.5,
      active_services: 8,
      pending_orders: 5,
      completed_orders: 142,
      last_updated: new Date().toISOString()
    };
  }

  private getMockMetrics(): any {
    return {
      daily_revenue: [850, 920, 1100, 1250, 980, 1350, 1200],
      customer_growth: [10, 15, 8, 22, 18, 25, 12],
      service_usage: {
        'lavagem_simples': 45,
        'lavagem_completa': 32,
        'enceramento': 18,
        'aspiracao': 28,
        'detailing': 12
      },
      popular_services: ['lavagem_completa', 'lavagem_simples', 'aspiracao'],
      revenue_by_service: {
        'lavagem_simples': 8500,
        'lavagem_completa': 12800,
        'enceramento': 5400,
        'aspiracao': 3200,
        'detailing': 4800
      }
    };
  }
}
